import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchPostBySlug, fetchBlogById, deletePost } from '../store/blogSlice';
import { selectCurrentPost, selectCurrentBlog } from '../store/selectors';
import { selectUser } from '@/features/auth/store/selectors';
import { ShareButton } from '../components/ShareButton';
import { LightboxGallery, type GalleryItem } from '../components/LightboxGallery';
import { Button } from '@/components/ui/button';
import { Loading } from '@/components/ui/loading';
import { Calendar, Edit, Folder, ChevronRight, Trash2, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { blogAPI } from '../api/blogApi';
import type { PostImage } from '../types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const PostDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const post = useAppSelector(selectCurrentPost);
  const blog = useAppSelector(selectCurrentBlog);
  const user = useAppSelector(selectUser);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [images, setImages] = useState<PostImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const isAdmin = useMemo(() => {
    if (!blog || !user) return false;
    return !!(
      String(blog.owner_id) === String(user.id) ||
      (blog.admins && blog.admins.some((adminId) => String(adminId) === String(user.id)))
    );
  }, [blog, user]);

  useEffect(() => {
    if (slug) {
      dispatch(fetchPostBySlug(slug));
    }
  }, [slug, dispatch]);

  // Fetch blog when we have post
  useEffect(() => {
    if (post && post.blog_id) {
      // Always fetch to ensure we have latest blog data
      dispatch(fetchBlogById(post.blog_id));
    }
  }, [post, dispatch]);

  // Fetch images when we have post
  useEffect(() => {
    if (post && post.id) {
      setLoadingImages(true);
      blogAPI.public.getPostImages(post.id)
        .then((response) => {
          if (response.success && response.data) {
            // Sort by sort_order
            const sortedImages = [...response.data].sort((a, b) =>
              (a.sort_order ?? 0) - (b.sort_order ?? 0)
            );
            setImages(sortedImages);
          }
        })
        .catch((error) => {
          console.error('Failed to load post images:', error);
        })
        .finally(() => {
          setLoadingImages(false);
        });
    }
  }, [post]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm');
    } catch {
      return dateString;
    }
  };

  const handleDelete = async () => {
    if (!post || !blog) return;

    setIsDeleting(true);
    try {
      await dispatch(deletePost(post.id)).unwrap();
      toast.success('Đã xóa bài viết thành công');
      navigate(`/blogs/${blog.slug}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Không thể xóa bài viết';
      toast.error(message);
      setIsDeleting(false);
    }
  };

  if (!post) {
    return <Loading text="Đang tải bài viết..." description="Vui lòng đợi trong giây lát" />;
  }


  return (
    <div className="flex-1 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header with Blog Name and Close Button */}
          {blog && (
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Link
                  to={`/blogs/${blog.slug}`}
                  className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                >
                  {blog.title}
                </Link>
                {post.category && (
                  <>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                    <Link
                      to={`/blogs/${blog.slug}?category=${post.category.id}`}
                      className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      {post.category.name}
                    </Link>
                  </>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/blogs/${blog.slug}`)}
                className="rounded-full h-8 w-8 p-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          )}

          {/* Post Header */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {post.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 pb-4 border-b">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {post.published_at
                    ? formatDate(post.published_at)
                    : formatDate(post.created_at)}
                </span>
              </div>

              {post.category && (
                <div className="flex items-center gap-2">
                  <Folder className="h-4 w-4" />
                  <Link
                    to={`/blogs/${blog?.slug}`}
                    className="hover:text-blue-600 transition-colors"
                  >
                    {post.category.name}
                  </Link>
                </div>
              )}
            </div>

            {/* Admin Actions */}
            {isAdmin && (
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/posts/${post.slug}/edit`)}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Chỉnh sửa
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Xóa
                </Button>
              </div>
            )}
          </div>

          {/* Post Content */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="prose prose-lg max-w-none">
              <div
                className="whitespace-pre-wrap"
                dangerouslySetInnerHTML={{
                  __html: post.content
                    .replace(/\n/g, '<br />')
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\*(.*?)\*/g, '<em>$1</em>')
                    .replace(/#{1,6}\s+(.*?)(\n|$)/g, (match, p1) => {
                      const level = match.trim().split(' ')[0].length;
                      return `<h${level}>${p1}</h${level}>`;
                    }),
                }}
              />
            </div>

            {/* Post Images and Videos */}
            {loadingImages && (
              <div className="mt-8 text-center text-gray-500">
                <Loading text="Đang tải media..." />
              </div>
            )}

            {!loadingImages && images.length > 0 && (
              <div className="mt-8 space-y-6">
                {images.map((image, index) => (
                    <div
                      key={image.id}
                      className="cursor-pointer"
                      onClick={() => {
                        setLightboxIndex(index);
                        setLightboxOpen(true);
                      }}
                    >
                      {image.type === 'video' ? (
                        <div className="relative w-full bg-black" style={{ paddingBottom: '56.25%' }}>
                          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                            <div className="text-center">
                              <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="h-10 w-10 text-gray-900 ml-2" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </div>
                              <p className="text-white text-sm font-medium">Click để xem video</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={image.url}
                          alt={image.caption || `Image ${(image.sort_order ?? 0) + 1}`}
                          className="w-full shadow-md hover:shadow-xl transition-shadow"
                          loading="lazy"
                        />
                      )}
                      {image.caption && (
                        <p className="text-sm text-gray-600 text-center italic mt-2">
                          {image.caption}
                        </p>
                      )}
                    </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Button */}
      <ShareButton
        url={window.location.href}
        title={post.title}
        description={blog ? `${post.title} - ${blog.title}` : post.title}
        brandName={blog?.title} // Show blog name for posts
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          if (!isDeleting) setShowDeleteDialog(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa bài viết</AlertDialogTitle>
            <AlertDialogDescription>
              {isDeleting
                ? 'Đang xóa bài viết và các tệp phương tiện liên quan...'
                : `Bạn có chắc chắn muốn xóa bài viết "${post.title}" không? Hành động này không thể hoàn tác.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Đang xóa...
                </>
              ) : 'Xóa bài viết'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lightbox Gallery */}
      <LightboxGallery
        items={images as GalleryItem[]}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};
